import React from 'react'

import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_users_quarantine'

function UsersQuarantineI18n({ classes, productName }: { classes: ReturnType<typeof styles>; productName?: string }) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatValues = { productName, hostname: window.location.hostname }

  return (
    <div className={classes.content} data-testid="content">
      <p>{formatMessage('intro')}</p>

      <ul>
        <li>
          <b>{formatMessage('settings.quarantine_intent_analysis.label')}</b>
          <br />
          {formatMessage('settings.quarantine_intent_analysis.description')}
        </li>
        <li>
          <b>{formatMessage('settings.default_interval.label')}</b>
          <br />
          {formatMessage('settings.default_interval.description')}
        </li>
        <ul>
          <li>
            <b>{formatMessage('settings.default_interval.never.label')}</b>
            <br />
            {formatMessage('settings.default_interval.never.description')}
          </li>
          <li>
            <b>{formatMessage('settings.default_interval.scheduled.label')}</b>
            <br />
            {formatMessage('settings.default_interval.scheduled.description')}
          </li>
        </ul>
        <li>
          <b>{formatMessage('settings.allow_users_specify.label')}</b>
          <br />
          {formatMessage('settings.allow_users_specify.description')}
        </li>
      </ul>
      <p>{formatMessage('link_validity', formatValues)}</p>
      <p>{formatMessage('require_login_intro')}</p>

      <ul>
        <li>
          <b>{formatMessage('require_login.label')}</b>
          <br />
          {formatMessage('require_login.description')}
        </li>
      </ul>
    </div>
  )
}

function UsersQuarantine(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <UsersQuarantineI18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <p>
        You can select to send user quarantine notifications at scheduled intervals. Note that quarantine notifications
        are sent only if new mail is quarantined since the last notice.{' '}
      </p>

      <ul>
        <li>
          <b>Quarantine messages for Intent Analysis</b>
          <br />
          When set to <b>Yes</b>, messages that would be blocked by Intent Analysis will be quarantined instead.
        </li>
        <li>
          <b>Default interval for user quarantine notifications</b>
          <br />
          Specify whether to <b>Never</b> send user quarantine notifications or create <b>Scheduled</b> user quarantine
          notifications.
        </li>
        <ul>
          <li>
            <b>Never</b>
            <br />
            Mail is quarantined but no notification is sent.
          </li>
          <li>
            <b>Scheduled</b>
            <br />
            Select the day and time quarantine notifications are to be sent using the{' '}
            <b>Schedule notification intervals</b> section.
          </li>
        </ul>
        <li>
          <b>Allow users to specify interval</b>
          <br />
          When set to <b>Yes</b>, users can override these settings and configure their own notification interval.
        </li>
      </ul>
      <p>
        The links in the quarantine notification email allow the user to access their {props.productName} user account
        without entering their username and password. The link is valid for seven days, after which the user must log in
        manually at https://{window.location.hostname}.
      </p>
      <p>
        You can require users to enter their login credentials before they can access quarantined messages. By default,
        this is set to <b>No</b>. Note that this option is available to account admins only and does not display when
        logged in as a domain admin.
      </p>

      <ul>
        <li>
          <b>Require login credentials to access quarantined messages</b>
          <br />
          When set to <b>Yes</b>, users will be prompted to enter their account credentials before they can view their
          quarantined messages.
        </li>
      </ul>
    </div>
  )
}

export default UsersQuarantine
